import {
  selectOrganizationId,
  useDeleteTourFrameMutation,
  useRestoreTourFrameMutation,
  useUpdateTourFrameFrameTypeMutation,
  useUpdateTourFrameIsActiveMutation,
  useUpdateTourFrameStepsMutation,
  useUpdateTourFrameTitleMutation,
} from '@carboncopyco/data';
import { getRrwebIframe, getTargetElement } from '@carboncopyco/mimesis-shared';
import {
  FrameType,
  RelativePositionAlignmentEnums,
  RelativePositionTypeEnums,
  type Tour,
  type TourFrame,
  type TourStep,
} from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import type React from 'react';
import { useSelector } from 'react-redux';
import { useTourDispatch } from '../../../../../hooks';
import { useNotify } from '../../../../../hooks/useNotify';
import {
  selectAddingStepInProgress,
  setAspectRatio,
  setFocusFrameIndex,
} from '../../../../../store';
import { useTourEditorPageProps } from '../../../TourEditorPageContext';
import type { ReplayerEditorRef } from '../../../ToursReplayerEditor';
import useAddStepActions from './AddStep/useAddStepActions';

interface Props {
  tourId: number;
  frame: TourFrame;
  frames: TourFrame[];
  isFrameActive: boolean;
  activeFrameId: number;
  activeStepId: string;
  replayerRef?: React.RefObject<ReplayerEditorRef>;
  steps: TourStep[];
  frameType: FrameType;
  onFrameClick: (isEditMode: boolean) => void;
  doReplay: (step: TourStep) => void;
  fileServiceApiToken: Tour['fileServiceToken'];
}

const useFrameActions = ({
  tourId,
  frames,
  frame,
  isFrameActive,
  activeFrameId,
  activeStepId,
  replayerRef,
  steps,
  frameType,
  onFrameClick,
  doReplay,
  fileServiceApiToken,
}: Props) => {
  const { uuid, rootElement } = useTourEditorPageProps();

  const frameId = frame.id;
  const frameIndex = frame.frameIndex;

  const organizationId = useSelector(selectOrganizationId);
  const dispatch = useTourDispatch();
  const { trackEvent } = useAnalytics();
  const { showNotify } = useNotify(rootElement);

  const [updateTourFrameFrameType] = useUpdateTourFrameFrameTypeMutation();
  const [updateFrameTitleMutation] = useUpdateTourFrameTitleMutation();
  const [updateFrameIsActive] = useUpdateTourFrameIsActiveMutation();
  const [deleteFrame, { isLoading: isDeleteLoading }] =
    useDeleteTourFrameMutation();
  const [restoreFrame] = useRestoreTourFrameMutation();
  const [updateTourSteps] = useUpdateTourFrameStepsMutation();

  const isAddingStepInProgress = useSelector(selectAddingStepInProgress(uuid));

  const { cancelAddingStep } = useAddStepActions({
    uuid,
    frameId: activeFrameId,
    tourId: Number(tourId),
    replayerRef: replayerRef,
  });

  const handleFrameTitleUpdate = (newFrameTitle: string, frameId: number) => {
    updateFrameTitleMutation({
      organizationId,
      tourId: Number(tourId),
      frameId,
      frameTitle: newFrameTitle,
    });
    trackEvent(AnalyticsTrackEventName.TOUR_RENAME_FRAME, {
      location: 'tour_editor_sidebar_frame_name',
      tourId,
      frameId,
    });
  };

  const handleOnFrameClick = (
    event: React.MouseEvent<HTMLDivElement>,
    id: number,
    selected: boolean,
    firstStep: TourStep
  ) => {
    event.stopPropagation();
    if (isAddingStepInProgress) {
      cancelAddingStep(null, true);
    }

    if (selected) {
      /*
        Means you clicked on an already selected frame. Uncomment next line to
        enable frame edit mode (Editour)
      */
      // onFrameClick(true)
      return;
    }
    if (firstStep) {
      doReplay(firstStep);
    } else {
      const frameToReplay = frames.find((frame) => frame.id === id);
      replayerRef.current.deleteStep();
      replayerRef.current.playFrame(frameToReplay);
    }
    onFrameClick(false);
  };

  const handleToggleActive = () => {
    const newActiveState = !isFrameActive;

    if (frameId === activeFrameId && activeStepId) {
      replayerRef?.current?.refreshTooltip();
    }
    updateFrameIsActive({
      organizationId,
      tourId,
      frameId,
      isActive: newActiveState,
    })
      .unwrap()
      .then(() => {
        replayerRef?.current?.refreshTooltip();
      })
      .catch(() => {
        if (activeStepId) {
          replayerRef?.current?.refreshTooltip();
        }
      });

    trackEvent(AnalyticsTrackEventName.TOUR_TOGGLE_FRAME_VISIBILITY, {
      location: 'tour_editor_frame_context_menu',
      tourId,
      frameId,
      checkedItem: newActiveState,
    });
  };

  const handleDeleteFrame = () => {
    deleteFrame({ organizationId, id: frameId })
      .unwrap()
      .then(() => {
        if (activeFrameId === frameId) {
          dispatch(
            setFocusFrameIndex({
              uuid: uuid,
              frameIndex: frameIndex,
            })
          );
        }

        showNotify({
          message: 'Frame was deleted',
          actionText: 'Restore',
          onActionClick: () => restoreDeletedFrame(frameId),
        });
      })
      .catch(() => {
        notify.error(
          'There was an error deleting the frame, please try again.'
        );
      });

    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_DELETE_FRAME, {
      location: 'tour_editor_frame_context_menu',
      tourId,
      frameId,
    });
  };

  const restoreDeletedFrame = (deletedFrameId: number) => {
    restoreFrame({ organizationId, id: deletedFrameId })
      .unwrap()
      .catch(() => {
        notify.error("The frame couldn't be restored.");
      });

    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_RESTORE_FRAME, {
      location: 'tour_editor_snackbar',
      tourId,
      frameId,
    });
  };

  const handleConvertToFrame = () => {
    // check if frameScreenshotUrl is valid image before converting
    const img = new Image();
    img.src = `${frame.frameScreenshotUrl}?token=${fileServiceApiToken}`;
    img.addEventListener('load', convertFrame);
    img.addEventListener('error', () => {
      notify.error('Unable to convert frame to screenshot');
    });

    function convertFrame() {
      const newFrameType =
        frameType === FrameType.recording
          ? FrameType.screenshot
          : FrameType.recording;

      // It's important to navigate to frame we want to convert
      replayerRef.current.playFrame(frame);

      if (newFrameType === FrameType.screenshot) {
        dispatch(
          setAspectRatio({
            uuid: uuid,
            aspectRatio: 'fixedWidthAndHeight',
          })
        );
      }

      // We need to wait so that dom is updated in order to pick correct values for targetElement
      setTimeout(() => {
        const newTourSteps = steps.map((step) => {
          let newElementPosition = step.elementPosition || null;
          let layout = step.config.tooltip.balloon.layout;

          let oldLayout;
          const layoutBeforeConverting =
            step.config.tooltip.balloon.layoutBeforeConverting;

          if (newFrameType === FrameType.screenshot && !step.elementPosition) {
            // create tooltip area based on initial element position when first time converting to screenshot frame

            // since in desktop with multiple tabs open we can have multiple iframes in document,
            // we want to search for iframe only in current tab (rootElement) instead of whole document
            const iframe = getRrwebIframe(rootElement?.current);

            const targetElement = getTargetElement({
              xpath: step.xpath,
              elementPosition: step.elementPosition,
              frameType,
              doc: iframe.contentDocument,
            }).targetElement;

            const elementPosition = targetElement.getBoundingClientRect();

            if (elementPosition.top > iframe.clientHeight) {
              trackEvent(
                AnalyticsTrackEventName.TOUR_CLICK_CONVERT_FRAME_TOOLTIP_OUTSIDE_SCREENSHOT_AREA,
                {
                  location: 'tour_editor_frame_context_menu',
                  tourId,
                  frameId,
                  stepId: step.stepId,
                }
              );

              layout = {
                alignment: RelativePositionAlignmentEnums.middleCenter,
                relation: RelativePositionTypeEnums.page,
              };

              oldLayout = step.config.tooltip.balloon.layout;
            } else {
              newElementPosition = {
                xTopLeft: elementPosition.left,
                yTopLeft: elementPosition.top,
                width: elementPosition.width,
                height: elementPosition.height,
              };
            }
          }

          return {
            ...step,
            elementPosition: newElementPosition,
            config: {
              ...step.config,
              tooltip: {
                ...step.config.tooltip,
                balloon: {
                  ...step.config.tooltip.balloon,
                  layout: layoutBeforeConverting || {
                    ...layout,
                  },
                  layoutBeforeConverting: {
                    ...(oldLayout || layout),
                  },
                },
              },
            },
          };
        });

        updateTourFrameFrameType({
          organizationId,
          tourId,
          frameId,
          frameType: newFrameType,
          steps: newTourSteps,
        })
          .unwrap()
          .then(() => {
            notify.success('Frame successfully converted');
          })
          .catch(() => {
            notify.error(
              'There was an error converting the frame, please try again.'
            );
          });

        trackEvent(AnalyticsTrackEventName.TOUR_CLICK_CONVERT_FRAME, {
          location: 'tour_editor_frame_context_menu',
          tourId,
          frameId,
          variables: {
            convertTo: newFrameType,
          },
        });
      }, 500);
    }
  };

  const handleStepsUpdate = (steps: TourStep[]) => {
    updateTourSteps({
      organizationId,
      tourId,
      frameId,
      steps,
    });

    trackEvent(AnalyticsTrackEventName.TOUR_DRAG_REORDER_STEP, {
      location: 'tour_editor_frame_context_menu',
      tourId,
      frameId,
    });
  };

  return {
    handleFrameTitleUpdate,
    handleOnFrameClick,
    handleToggleActive,
    handleDeleteFrame,
    handleConvertToFrame,
    handleStepsUpdate,
    isDeleteLoading,
  };
};

export default useFrameActions;
